/**
 *
 * SignUpStepOne
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import FormControl from '@material-ui/core/FormControl';
import { CAPTION } from 'containers/Signup/constants';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import FacebookButton from 'components/SocialButtons/FacebookButton';
import GoogleButton from 'components/SocialButtons/GoogleButton';
import MicrosoftButton from 'components/SocialButtons/MicrosoftButton';
import Colors from 'utils/colors';

const useStyles = makeStyles(theme => ({
  caption: {
    marginTop: 10,
  },
  captionLink: {
    color: Colors.link,
    fontFamily: 'MadaMedium',
    cursor: 'pointer',
    '&:focus': {
      outlineWidth: 0,
    },
  },
  socialLoginContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  dividerText: {
    paddingLeft: 8,
    paddingRight: 8,
    width: '50%',
    textAlign: 'center',
    ...theme.typography.pxSmall,
    fontSize: '0.875rem',
  },
  formContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  labelCheck: {
    fontSize: 11,
    lineHeight: 'unset',
  },
  marginNone: {
    margin: 0,
    padding: 0,
  },
  checkboxApply: {
    padding: '0',
    paddingRight: 5,
  },
  textFieldContainer: {
    marginBottom: 16,
    '& label': {
      fontSize: '1.063rem',
    },
    '& input': {
      fontSize: '1.063rem',
    },
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    // marginTop: 16,
    marginBottom: 5,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  disclaimerWrapper: {
    margin: '20px 0',
    padding: 20,
    border: '1px solid #E9ECED',
    borderRadius: 5,
    position: 'relative',
  },
  disclaimerBar: {
    position: 'absolute',
    top: -1,
    left: -1,
    bottom: -1,
    width: 4,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    backgroundColor: ({ clientColor }) => `#${clientColor}`,
  },
}));

const SignUpStepOne = ({
  authOptions,
  customDisclaimer,
  disclaimerColor,
  email,
  password,
  showPassword,
  onCaptionClick,
  onChange,
  onSocialLogin,
  onSubmit,
  onTogglePasswordVisibility,
}) => {
  const classes = useStyles({ clientColor: disclaimerColor || '#3D9733' });

  const showCustomDisclaimer = !_isEmpty(customDisclaimer);

  return (
    <>
      <Grid item xs={12}>
        <Typography
          variant="caption"
          align="center"
          className={classes.caption}
        >
          {CAPTION.map(el => {
            if (el.action) {
              return (
                <span
                  key={el.value}
                  role="link"
                  tabIndex={0}
                  onKeyPress={() => onCaptionClick(el.action)}
                  className={classes.captionLink}
                  onClick={() => onCaptionClick(el.action)}
                >
                  {el.value}
                </span>
              );
            }

            return el.value;
          })}
        </Typography>
      </Grid>
      {(authOptions.facebook || authOptions.google || authOptions.ms) && (
        <Grid item xs={12} className={classes.socialLoginContainer}>
          {authOptions.facebook && (
            <FacebookButton onClick={() => onSocialLogin('facebook')} />
          )}
          {authOptions.google && (
            <GoogleButton onClick={() => onSocialLogin('google')} />
          )}
          {authOptions.ms && (
            <MicrosoftButton onClick={() => onSocialLogin('microsoft.com')} />
          )}
        </Grid>
      )}
      {(authOptions.facebook || authOptions.google || authOptions.ms) &&
        authOptions.cm && (
          <Grid item xs={12} className={classes.divider}>
            <div className={classes.dividerItem} />
            <Typography className={classes.dividerText}>
              or sign up with email
            </Typography>
            <div className={classes.dividerItem} />
          </Grid>
        )}
      {showCustomDisclaimer && (
        <div className={classes.disclaimerWrapper}>
          <div className={classes.disclaimerBar} />
          <RichTextReactRenderer
            paragraphVariant="overline"
            data={customDisclaimer.json}
            color="rgba(0, 0, 0, 0.87)"
          />
        </div>
      )}
      {authOptions.cm && (
        <Grid item xs={12}>
          <form className={classes.formContainer} onSubmit={onSubmit}>
            <TextField
              name="email"
              classes={{
                root: classes.textFieldContainer,
              }}
              fullWidth
              label="Email"
              value={email}
              onChange={onChange}
              type="email"
              required
              autoComplete="off"
            />
            <FormControl fullWidth required>
              <InputLabel htmlFor="adornment-password">Password</InputLabel>
              <Input
                id="adornment-password"
                name="password"
                classes={{
                  root: classes.textFieldContainer,
                }}
                autoComplete="off"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={onChange}
                endAdornment={
                  <InputAdornment position="end">
                    <FormControlLabel
                      classes={{
                        label: classes.labelCheck,
                        root: classes.marginNone,
                      }}
                      control={
                        <Checkbox
                          disableRipple
                          inputProps={{ 'aria-label': 'Show Password' }}
                          classes={{ root: classes.checkboxApply }}
                          checkedIcon={
                            <CheckBoxIcon
                              color="primary"
                              style={{ fontSize: 14 }}
                            />
                          }
                          icon={
                            <CheckBoxOutlineBlankIcon
                              style={{ fontSize: 14 }}
                            />
                          }
                          checked={showPassword}
                          onChange={onTogglePasswordVisibility}
                        />
                      }
                      label={showPassword ? 'Hide' : 'Show'}
                    />
                  </InputAdornment>
                }
              />
            </FormControl>
            <ButtonBase className={classes.button} type="submit">
              CONTINUE
            </ButtonBase>
          </form>
        </Grid>
      )}
    </>
  );
};

SignUpStepOne.propTypes = {
  authOptions: PropTypes.object,
  email: PropTypes.string,
  onCaptionClick: PropTypes.func,
  onChange: PropTypes.func,
  onSocialLogin: PropTypes.func,
  onSubmit: PropTypes.func,
  onTogglePasswordVisibility: PropTypes.func,
  password: PropTypes.string,
  customDisclaimer: PropTypes.object,
  disclaimerColor: PropTypes.string,
  showPassword: PropTypes.bool,
};

export default SignUpStepOne;
